/**
 * <bundle-plan-cards>
 * Toggles between the plan cards rendered by sections/main-product-bundle.liquid
 * (e.g. "Subscribe & Save" vs "Buy once"), keeps the product form's hidden
 * `selling_plan` input and the CTA button label in sync with the active card.
 *
 * Also redirects straight to checkout after a successful add, instead of the
 * theme's default cart-drawer-open behaviour — matching assets/bundle-page.js's
 * own CTA, which always does `window.location.href = '/checkout'` after
 * adding. This form keeps the standard `is="product-form"` (assets/theme.js's
 * ProductForm) for its free aria-busy loader / aria-disabled / error-message
 * handling rather than reimplementing that, but ProductForm is shared
 * sitewide and unconditionally ends a successful submit with
 * `this.cartDrawer?.show(...)`, so it can't be told to skip that step. Since
 * ProductForm's constructor (and its `submit` listener) runs during initial
 * custom-element upgrade — before this deferred script can register a
 * same-target listener early enough to pre-empt it — intercepting the
 * `submit` event itself isn't an option.
 *
 * Just redirecting from the `ajaxProduct:added` event (which fires before
 * `cartDrawer.show()`) isn't enough either: `window.location.href = ...`
 * only *schedules* navigation, it doesn't stop the rest of the synchronous
 * `.then()` callback from running, so `cartDrawer.show()` still executes and
 * visibly opens the drawer (confirmed by the user) before the browser
 * actually unloads the page for the redirect. So this instead no-ops
 * `<cart-drawer>`'s own `show()` method for the duration of this form's
 * submission (nothing else in the theme calls `.show()` — `cart.js`'s other
 * `cartUpdate` subscribers only re-render cart contents, they never open the
 * drawer themselves — so this fully prevents it rather than racing it) and
 * restores the real method once the attempt resolves, tagging only *this*
 * form's own submissions via a flag set in a plain (non-blocking) `submit`
 * listener so an unrelated add elsewhere on the page never no-ops the drawer
 * for other components.
 */
class BundlePlanCards extends HTMLElement {
  constructor() {
    super();
    this.onChange = this.onChange.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onAjaxProductAdded = this.onAjaxProductAdded.bind(this);
    this.onAjaxProductError = this.onAjaxProductError.bind(this);
  }

  connectedCallback() {
    this.radios = Array.from(this.querySelectorAll('[data-bundle-plan-radio]'));
    this.sellingPlanInput = this.querySelector('[data-bundle-selling-plan-input]');
    this.ctaPriceEl = this.querySelector('[data-bundle-cta-price]');

    this.radios.forEach((radio) => radio.addEventListener('change', this.onChange));

    const checked = this.radios.find((radio) => radio.checked) || this.radios[0];
    if (checked) this.applyCard(checked);

    this.form = this.closest('form');
    this.form?.addEventListener('submit', this.onFormSubmit);
    document.addEventListener('ajaxProduct:added', this.onAjaxProductAdded);
    document.addEventListener('ajaxProduct:error', this.onAjaxProductError);
  }

  disconnectedCallback() {
    this.radios.forEach((radio) => radio.removeEventListener('change', this.onChange));
    this.form?.removeEventListener('submit', this.onFormSubmit);
    document.removeEventListener('ajaxProduct:added', this.onAjaxProductAdded);
    document.removeEventListener('ajaxProduct:error', this.onAjaxProductError);
    this.restoreCartDrawer();
  }

  onChange(event) {
    this.applyCard(event.currentTarget);
  }

  onFormSubmit() {
    this.redirectToCheckoutOnAdd = true;
    this.suppressCartDrawer();
  }

  onAjaxProductAdded() {
    if (!this.redirectToCheckoutOnAdd) return;
    this.redirectToCheckoutOnAdd = false;
    window.location.href = '/checkout';
    // Leave the drawer suppressed: the page is unloading. If that redirect
    // is ever interrupted, restoring it anyway would reopen a drawer the
    // customer never asked to see mid-navigation.
  }

  onAjaxProductError() {
    this.redirectToCheckoutOnAdd = false;
    this.restoreCartDrawer();
  }

  suppressCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || this.originalCartDrawerShow) return;
    this.originalCartDrawerShow = cartDrawer.show.bind(cartDrawer);
    cartDrawer.show = () => {};
  }

  restoreCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || !this.originalCartDrawerShow) return;
    cartDrawer.show = this.originalCartDrawerShow;
    this.originalCartDrawerShow = null;
  }

  applyCard(radio) {
    this.radios.forEach((item) => {
      const card = item.closest('[data-bundle-card]');
      if (card) card.classList.toggle('is-selected', item === radio);
    });

    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = radio.dataset.sellingPlanId || '';
    }

    if (this.ctaPriceEl && radio.dataset.price) {
      this.ctaPriceEl.textContent = radio.dataset.price;
    }
  }
}

customElements.define('bundle-plan-cards', BundlePlanCards);
